// What the "i" beside each automation policy and notification setting
// says. Each entry describes what the platform does today.

import type { InfoTipContent } from '@cyber-aware/ui';

export const POLICY_INFO = {
  status: {
    title: 'Status',
    description: 'Whether this policy runs, and whether it’s allowed to act. Try a new policy in dry-run first and check what it would have done.',
    options: [
      { name: 'Draft', description: 'Saved but never evaluated.' },
      { name: 'Dry-run', description: 'Evaluated on real activity, with no action taken.' },
      { name: 'Live', description: 'Evaluated, and its action runs on anyone who matches.' },
      { name: 'Paused', description: 'Stopped until you change it back.' },
    ],
  },
  action: {
    title: 'Action',
    description: 'What the policy does to a match when it’s live. Actions on Microsoft Entra ID, Okta or X-One Umbra need that connection in place.',
    options: [
      { name: 'Log only', description: 'Records the match and changes nothing.' },
      { name: 'Notify admin', description: 'Tells your admins about the match.' },
      { name: 'Assign training', description: 'Assigns your remediation training module.' },
      { name: 'Suspend membership', description: 'Suspends the person in this organization and signs them out until an admin restores them. The last active owner is skipped.' },
    ],
  },
  window: {
    title: 'Window and threshold',
    description: 'How much activity it takes to match. For example, a window of 30 days with “>= 4” matches at the fourth event within 30 days.',
  },
  scope: {
    title: 'Scope',
    description: 'How events are counted toward the threshold.',
    options: [
      { name: 'Per user', description: 'Each person is counted separately, so the action applies to the person who matched.' },
      { name: 'Per organization', description: 'Everyone’s events are counted together.' },
    ],
  },
  cooldown: {
    title: 'Cooldown',
    description: 'The minimum gap before this policy can act on the same person again, so someone who keeps matching isn’t hit repeatedly. Use 0 for no gap. It doesn’t apply to policies scoped to the organization.',
  },
  defaultCooldown: {
    title: 'Default cooldown',
    description: 'Pre-fills the cooldown on new policies. Each policy keeps its own value, so changing this doesn’t affect existing ones.',
  },
  remediationModule: {
    title: 'Default module',
    description: 'The training assigned by every policy whose action is Assign training. With none chosen, those policies skip the assignment.',
  },
} as const satisfies Record<string, InfoTipContent>;

export const NOTIFICATION_INFO = {
  channels: {
    title: 'Where notifications go',
    description: 'Choose each channel per notification type. Your choices apply in every organization you belong to, and each change saves right away.',
    options: [
      { name: 'In app', description: 'Appears under the bell in the top bar.' },
      { name: 'Email', description: 'Sent to the address on your account.' },
    ],
  },
} as const satisfies Record<string, InfoTipContent>;
